import React, { useState } from 'react';
import NumberFormat from 'react-number-format';
import { Card, CardBody, Col, Row } from 'reactstrap';
import {
    BarChart,
    ResourceUseFacilities,
    ResourceUseDistricts,
    ResourceUseProvinces,
} from 'components';
import { GoogleSheetsProvider } from 'providers';

const ResourceUseTable = (props) => {
    switch (props.view) {
        case 'facilities': return <ResourceUseFacilities {...props} />
        case 'districts': return <ResourceUseDistricts {...props} />
        case 'provinces': return <ResourceUseProvinces {...props} />
        default: return null;
    }
};

const ResourceUseStats = props => {
    const { view, loaded, query, province } = props;
    const [tableData, setTableData] = useState([]);
    const [chartData, setChartData] = useState({});
    const [componentLoaded, setComponentLoaded] = useState(false);
    const [redraw, setRedraw] = useState(false);
    const [filterTab, setFilterTab] = useState('all');
    
    const fetchData = async () => {
        if (loaded) {
            const labels = [];
            const data = await GoogleSheetsProvider.provinces();
            setTableData(data);

            if (data && data.length > 0) {
                const allBedsData = [];
                const icuBedsData = [];
                const bedShortData = [];
                const testingSitesData = [];
                data.map(item => {
                    labels.push(item.iso_code);
                    allBedsData.push(item.all_bed_avail);
                    icuBedsData.push(item.icu_bed_tot);
                    bedShortData.push(item.all_bed_short);
                    testingSitesData.push(item.testing_sites);
                    return true;
                });
                const chartData = {
                    labels,
                    datasets: [
                        {
                            label: 'All Beds',
                            backgroundColor: 'rgba(1,104,250,0.7)',
                            borderColor: 'rgba(1,104,250,0.7)',
                            borderWidth: 1,
                            hoverBackgroundColor: 'rgba(1,104,250,1)',
                            hoverBorderColor: 'rgba(1,104,250,1)',
                            data: allBedsData,
                        },
                        {
                            label: 'ICU Beds',
                            backgroundColor: 'rgba(253,126,20,0.7)',
                            borderColor: 'rgba(253,126,20,0.7)',
                            borderWidth: 1,
                            hoverBackgroundColor: 'rgba(253,126,20,1)',
                            hoverBorderColor: 'rgba(253,126,20,1)',
                            data: icuBedsData,
                        },
                        {
                            label: 'Bed Shortages',
                            backgroundColor: 'rgba(255,99,132,0.7)',
                            borderColor: 'rgba(255,99,132,0.7)',
                            borderWidth: 1,
                            hoverBackgroundColor: 'rgba(255,99,132,1)',
                            hoverBorderColor: 'rgba(255,99,132,1)',
                            data: bedShortData,
                        },
                    ]
                };
                if (labels.length > 0) {
                    setChartData(chartData);
                }
            }
            setComponentLoaded(true);
        }
    };

    if (loaded) {
        if (!componentLoaded) {
            fetchData();
        }
    }

    const reDrawChart = (activeTab) => {
        const labels = [];
        const datasets = [];
        const tData = tableData;
        if (tData.length > 0) {
            const allBedsData = [];
            const icuBedsData = [];
            const bedShortData = [];
            const testingSitesData = [];
            tData.map(item => {
                labels.push((view === 'provinces') ? item.iso_code : item.code);
                if (activeTab === 'all' || activeTab === 'all_bed_avail')
                    allBedsData.push(item.all_bed_avail);
                if (activeTab === 'all' || activeTab === 'icu_bed_tot')
                    icuBedsData.push(item.icu_bed_tot);

                bedShortData.push(item.all_bed_short);
                if (activeTab === 'all' || activeTab === 'testing_sites')
                    testingSitesData.push(item.testing_sites);
                return true;
            });

            // all beds
            if (activeTab === 'all' || activeTab === 'all_bed_avail') {
                datasets.push({
                    label: 'All Beds',
                    backgroundColor: 'rgba(1,104,250,0.7)',
                    borderColor: 'rgba(1,104,250,0.7)',
                    borderWidth: 1,
                    hoverBackgroundColor: 'rgba(1,104,250,1)',
                    hoverBorderColor: 'rgba(1,104,250,1)',
                    data: allBedsData,
                });
            }

            // icu beds
            if (activeTab === 'all' || activeTab === 'icu_bed_tot') {
                datasets.push({
                    label: 'ICU Beds',
                    backgroundColor: 'rgba(253,126,20,0.7)',
                    borderColor: 'rgba(253,126,20,0.7)',
                    borderWidth: 1,
                    hoverBackgroundColor: 'rgba(253,126,20,1)',
                    hoverBorderColor: 'rgba(253,126,20,1)',
                    data: icuBedsData,
                });
            }
            
            // bed shortages
            datasets.push({
                label: 'Bed Shortages',
                backgroundColor: 'rgba(255,99,132,0.7)',
                borderColor: 'rgba(255,99,132,0.7)',
                borderWidth: 1,
                hoverBackgroundColor: 'rgba(255,99,132,1)',
                hoverBorderColor: 'rgba(255,99,132,1)',
                data: bedShortData,
            });

            const chartData = { labels, datasets };

            if (labels.length > 0) {
                setChartData(chartData);
                setRedraw(true);
            }
        }
    };

    return (
        <Card className="mg-b-10">
            <div className="card-header pd-t-20 d-sm-flex align-items-start justify-content-between bd-b-0 pd-b-0">
                <div>
                    <h5 className="mg-b-5">
                        Hospital Resource Use Stats
                        <span className="tx-primary">{province !== 'all' ? ` for ${province}` : ''}</span>
                    </h5>
                    <p className="tx-13 tx-color-03 mg-b-0">Resources needed for COVID-19 patients</p>
                </div>
                <div className="d-flex mg-t-20 mg-sm-t-0">
                    <div className="btn-group flex-fill">
                        <button
                            className={`btn btn-white btn-uppercase btn-xs ${filterTab === 'all' ? 'active' : ''}`}
                            onClick={() => {
                                setFilterTab('all');
                                reDrawChart('all');
                            }}
                        >
                            All Resources
                        </button>
                        <button
                            className={`btn btn-white btn-uppercase btn-xs ${filterTab === 'all_bed_avail' ? 'active' : ''}`}
                            onClick={() => {
                                setFilterTab('all_bed_avail');
                                reDrawChart('all_bed_avail');
                            }}
                        >
                            All Beds
                        </button>
                        <button
                            className={`btn btn-white btn-uppercase btn-xs ${filterTab === 'icu_bed_tot' ? 'active' : ''}`}
                            onClick={() => {
                                setFilterTab('icu_bed_tot');
                                reDrawChart('icu_bed_tot');
                            }}
                        >
                            ICU Beds
                        </button>
                    </div>
                </div>
            </div>
            <CardBody className="pd-lg-25">
                <Row className="align-items-sm-end">
                    <Col lg={7} xl={8}>
                        {componentLoaded
                        && (
                        <div className="pd-lg-5" style={{ position: 'relative', minHeight: 300, width: '100%' }}>
                            <BarChart
                                chartData={chartData}
                                redraw={redraw}
                                setRedraw={setRedraw}
                            />
                        </div>)}
                    </Col>
                    <Col lg={5} xl={4} className="pd-b-80">
                        <Row>
                            <Col sm={6} lg={12}>
                                <div className="d-flex align-items-center justify-content-between mg-b-5">
                                    <h6 className="tx-uppercase tx-10 tx-spacing-1 tx-color-02 tx-semibold mg-b-0">All Beds</h6>
                                    <span className="tx-10 tx-color-04">Projected</span>
                                </div>
                                <div className="d-flex align-items-end justify-content-between mg-b-5">
                                    <h5 className="tx-normal tx-rubik lh-2 mg-b-0">
                                        <NumberFormat displayType={'text'} thousandSeparator value={props.all_bed_avail ? props.all_bed_avail : 0} />
                                    </h5>
                                    <h6 className="tx-normal tx-rubik tx-color-03 lh-2 mg-b-0">32</h6>
                                </div>
                                <div className="progress ht-4 mg-b-0 op-5">
                                    <div className="progress-bar bg-primary wd-100p" role="progressbar" aria-valuenow="100" aria-valuemin="0" aria-valuemax="100"></div>
                                </div>
                            </Col>
                            <Col sm={6} lg={12} className="mg-t-30 mg-sm-t-0 mg-lg-t-30">
                                <div className="d-flex align-items-center justify-content-between mg-b-5">
                                    <h6 className="tx-uppercase tx-10 tx-spacing-1 tx-color-02 tx-semibold mg-b-0">Bed Shortages</h6>
                                    <span className="tx-10 tx-color-04">Projected</span>
                                </div>
                                <div className="d-flex align-items-end justify-content-between mg-b-5">
                                    <h5 className="tx-normal tx-rubik lh-2 mg-b-0">
                                        <NumberFormat displayType={'text'} thousandSeparator value={props.all_bed_short ? props.all_bed_short : 0} />
                                    </h5>
                                    <h6 className="tx-normal tx-rubik tx-color-03 lh-2 mg-b-0">11</h6>
                                </div>
                                <div className="progress ht-4 mg-b-0 op-5">
                                    <div className="progress-bar bg-pink wd-20p" role="progressbar" aria-valuenow="20" aria-valuemin="0" aria-valuemax="100"></div>
                                </div>
                            </Col>
                            <Col sm={6} lg={12} className="mg-t-30 mg-sm-t-0 mg-lg-t-30">
                                <div className="d-flex align-items-center justify-content-between mg-b-5">
                                    <h6 className="tx-uppercase tx-10 tx-spacing-1 tx-color-02 tx-semibold mg-b-0">ICU Beds</h6>
                                    <span className="tx-10 tx-color-04">Projected</span>
                                </div>
                                <div className="d-flex align-items-end justify-content-between mg-b-5">
                                    <h5 className="tx-normal tx-rubik lh-2 mg-b-0">
                                        <NumberFormat displayType={'text'} thousandSeparator value={props.icu_bed_tot ? props.icu_bed_tot : 0} />
                                    </h5>
                                    <h6 className="tx-normal tx-rubik tx-color-03 lh-2 mg-b-0">89</h6>
                                </div>
                                <div className="progress ht-4 mg-b-0 op-5">
                                    <div className="progress-bar bg-orange wd-45p" role="progressbar" aria-valuenow="45" aria-valuemin="0" aria-valuemax="100"></div>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </CardBody>
            <ResourceUseTable tableData={tableData} view={view} />
        </Card>
    );
}

export default ResourceUseStats;
